import React, { useState, useEffect, useMemo } from "react";

import { useAppDispatch, useAppSelector } from "../../redux/hooks/redux-hooks";
import { fetchCompetitionIntensities } from "../../redux/actions/CompetitionIntensityActions";
import EnhancedTable from "../table/Table";
import PageTitle from "../PageTitle";
import PageSearch from "../PageSearch";
import DialogBox from "../../components/dialog-box/DialogBox";
import CompetitionIntensityForm from "./CompetitionIntensityForm";
import Loader from "../../components/loader/Loader";
import { CompetitionIntensityModel } from "../../models/CompetitionIntensityModel";
import { isValidArr } from "../../utility/utils";

export type Mode = "add" | "edit";

type iData = {
  id: number;
  name: string;
  description: string;
  // status: string;
};

const CompetitionIntensityPage = () => {
  const dispatch = useAppDispatch();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [mode, setMode] = useState<Mode>("add");
  const [editData, setEditData] = useState<CompetitionIntensityModel>();
  const [rootData, setRootData] = useState<iData[]>([]);
  const [filteredData, setFilteredData] = useState<iData[]>([]);

  const allData = useAppSelector(
    (state) => state.competitionIntensity.all_CompetitionIntensities
  );

  const isCompetitionIntensitiesFetching = useAppSelector(
    (state) => state.competitionIntensity.isCompetitionIntensitiesFetching
  );

  const columns = useMemo(() => {
    return [
      {
        id: "name",
        numeric: false,
        disablePadding: false,
        label: "Name",
      },
      {
        id: "description",
        numeric: false,
        disablePadding: false,
        label: "Description",
      },
      // {
      //   id: "status",
      //   numeric: false,
      //   disablePadding: false,
      //   label: "Status",
      // },
    ];
  }, []);

  //To fetch all competition intensities
  useEffect(() => {
    dispatch(fetchCompetitionIntensities());
  }, [dispatch]);

  // To prepare the root data
  useEffect(() => {
    setRootData(() =>
      allData.map((rec) => ({
        id: rec.competitionIntensityId,
        name: rec.name,
        description: rec.description,
        // status: rec.isActive ? "Active" : "Inactive",
      }))
    );
  }, [allData]);

  // To set filtered data on change root data
  useEffect(() => {
    setFilteredData(rootData);
  }, [rootData]);

  const handleEdit = (id: string | number) => {
    const editRec = allData.find((rec) => rec.competitionIntensityId === id);
    setMode("edit");
    setEditData(editRec);
    setIsModalOpen(true);
  };

  const createHandler = () => {
    setMode("add");
    setEditData(undefined);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const searchHandler = (searchVal: string): void => {
    setFilteredData(() =>
      searchVal
        ? rootData.filter((info) =>
            Object.values(info).join("").toLowerCase().includes(searchVal)
          )
        : rootData
    );
  };

  return (
    <>
      {!isValidArr(allData) && isCompetitionIntensitiesFetching ? (
        <Loader />
      ) : null}
      <PageTitle
        title="Competition Intensities"
        btnText="Add Competition Intensity"
        onClick={createHandler}
      />
      <PageSearch
        placeholder="Search Competition Intensities"
        searchHandler={searchHandler}
      />
      <EnhancedTable
        columns={columns}
        data={filteredData}
        handleEdit={handleEdit}
      />
      {isModalOpen && (
        <DialogBox
          isModalOpen={isModalOpen}
          modalTitle={
            mode === "add"
              ? "Create Competition Intensity"
              : "Edit Competition Intensity"
          }
          handleModalClose={handleModalClose}
        >
          <CompetitionIntensityForm
            mode={mode}
            data={editData}
            handleModalClose={handleModalClose}
          />
        </DialogBox>
      )}
    </>
  );
};

export default CompetitionIntensityPage;
